var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Suspense, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { FirebaseAuthContext } from 'libs.firebase_react';
import { Header, useLocalizeMessage, Icon, SideNav, Spinner, Text, } from 'libs.nucleus';
import { AclScope, AppBanner, TimezoneChangeDetectedModal, versionNumberToString } from 'libs.react';
import { ApiClientService, AuthContext, AuthEndpoint, AuthRoute, useApiClient, useProductTemplates, useScopeValidator, useUserRoles, } from 'libs.react_internal';
import { useSelfServiceNavigation } from '../../hooks';
import { getDontShowTimezoneChangeModal, setDontShowTimezoneChangeModal, } from '../../utils/timezone_modal_cookie.utils';
export const PrivateRoute = ({ sectionPath, navItems }) => {
    var _a, _b, _c;
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const iamClient = useApiClient(ApiClientService.IAM);
    const translate = useLocalizeMessage();
    const canUpdateWorkspace = useScopeValidator(AclScope.MOM_ENTITY_UPDATE);
    const { latestTemplateConfig } = useProductTemplates();
    const { isAdmin, isAIAdmin, isSiteUser } = useUserRoles();
    const isNotificationsPage = pathname === '/profile/notifications';
    const [timezoneChangeDetected, setTimezoneChangeDetected] = useState(false);
    const [hasEvaluatedTimezoneChange, setHasEvaluatedTimezoneChange] = useState(false);
    const [isConfigurationLoading, setIsConfigurationLoading] = useState(false);
    const [isDocumentationLoading, setIsDocumentationLoading] = useState(false);
    const { entity, getIDPConfiguration, userEntities, isLoading, isLoggedIn, logout: authLogout, navigateToLogin, ssoLogin, user, refreshIdentity, } = useContext(AuthContext);
    const firebaseAuth = useContext(FirebaseAuthContext);
    const { appRoutes } = useSelfServiceNavigation();
    const isAgentsAppEnabled = (_b = (_a = entity === null || entity === void 0 ? void 0 : entity.configuration) === null || _a === void 0 ? void 0 : _a.agentStudioEnabled) !== null && _b !== void 0 ? _b : false;
    const canAccessAgentSettings = isAgentsAppEnabled && (isAdmin || isAIAdmin);
    useEffect(() => {
        if (!isLoading && !isLoggedIn) {
            navigateToLogin();
        }
    }, [isLoggedIn, isLoading]);
    const onConfirmTimezoneChange = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        setTimezoneChangeDetected(false);
        yield iamClient.put(`${AuthEndpoint.IDENTITIES}/me`, {
            tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
        });
        yield refreshIdentity();
    }), [iamClient, refreshIdentity]);
    const onCloseTimezoneChange = () => {
        setTimezoneChangeDetected(false);
    };
    const onDontShowAgainTimezoneChange = () => {
        setDontShowTimezoneChangeModal();
    };
    useEffect(() => {
        const evaluateTimezoneChange = () => __awaiter(void 0, void 0, void 0, function* () {
            if (isLoading || hasEvaluatedTimezoneChange) {
                return;
            }
            if (!isLoggedIn || !user || getDontShowTimezoneChangeModal()) {
                setHasEvaluatedTimezoneChange(true);
                return;
            }
            if (!user.tz) {
                try {
                    yield onConfirmTimezoneChange();
                }
                finally {
                    setHasEvaluatedTimezoneChange(true);
                }
                return;
            }
            const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            setTimezoneChangeDetected(timezone !== user.tz);
            setHasEvaluatedTimezoneChange(true);
        });
        void evaluateTimezoneChange();
    }, [user, isLoading, isLoggedIn, onConfirmTimezoneChange]);
    const onWorkspaceChange = (userEntity) => __awaiter(void 0, void 0, void 0, function* () {
        setIsConfigurationLoading(true);
        try {
            const userEntities = yield getIDPConfiguration(user.email);
            const foundEntity = userEntities.find((entity) => entity.id === userEntity.id);
            const isSSOOnly = (foundEntity === null || foundEntity === void 0 ? void 0 : foundEntity.loginMethods.includes('sso')) && !(foundEntity === null || foundEntity === void 0 ? void 0 : foundEntity.loginMethods.includes('password'));
            if (foundEntity && isSSOOnly) {
                ssoLogin(foundEntity, user.email);
            }
            else {
                navigate(`${AuthRoute.SWITCH_WORKSPACE}/${userEntity.id}`, {
                    state: { entityIdp: foundEntity },
                });
            }
        }
        finally {
            setIsConfigurationLoading(false);
        }
    });
    const workspaceOptions = useMemo(() => {
        const defaultOption = {
            content: (_jsx(Text, { size: 'xs', className: 'font-semibold text-text-disabled', children: translate('YOUR WORKSPACES') })),
            id: 'title',
            readonly: true,
        };
        const options = userEntities.map((userEntity) => (Object.assign({ id: userEntity.id.toString(), content: userEntity.name, onClick: () => onWorkspaceChange(userEntity) }, ((entity === null || entity === void 0 ? void 0 : entity.id) === userEntity.id && { className: 'pointer-events-none' }))));
        const settingsOptions = [];
        if (canUpdateWorkspace) {
            settingsOptions.push({
                content: translate('Workspace settings'),
                id: 'ws-settings',
                onClick: () => navigate('/workspace/settings'),
            });
        }
        if (canAccessAgentSettings) {
            settingsOptions.push({
                content: translate('Agent settings'),
                id: 'agent-settings',
                onClick: () => window.open(`${process.env.AGENT_STUDIO_URL}/agent-studio/settings`),
            });
        }
        if (settingsOptions.length === 0) {
            return [defaultOption, ...options];
        }
        const settingsTitle = {
            content: (_jsx(Text, { size: 'xs', className: 'font-semibold text-text-disabled', children: translate('ADMIN SETTINGS') })),
            hasTopBorder: true,
            id: 'settings-title',
            readonly: true,
        };
        return [defaultOption, ...options, settingsTitle, ...settingsOptions];
    }, [canAccessAgentSettings, canUpdateWorkspace, entity === null || entity === void 0 ? void 0 : entity.id, navigate, translate, userEntities]);
    const headerNavLinks = Object.values(appRoutes)
        .filter(({ hideFromHeader }) => !hideFromHeader)
        .map(({ name, path, externalUrl }) => {
        const onClick = () => {
            if (externalUrl) {
                window.open(path);
            }
            else {
                navigate(path);
            }
        };
        return { name, path, onClick };
    });
    const openDeveloperFlags = () => {
        navigate('/developer/flags');
    };
    const goToProfilePage = () => {
        navigate('/profile/me');
    };
    const onNotificationsClick = () => {
        navigate('/profile/notifications');
    };
    const openDocumentation = (_a) => __awaiter(void 0, [_a], void 0, function* ({ event }) {
        event.preventDefault();
        if (!isDocumentationLoading) {
            setIsDocumentationLoading(true);
            const { data: response } = yield iamClient.get('/v1/gitbook/doc-url');
            setIsDocumentationLoading(false);
            if (response.url) {
                window.open(response.url, '_blank');
            }
        }
    });
    const platformVersion = useMemo(() => translate('R{version}', { version: versionNumberToString(latestTemplateConfig === null || latestTemplateConfig === void 0 ? void 0 : latestTemplateConfig.altName) }), [latestTemplateConfig, translate]);
    const dropdownOptions = useMemo(() => {
        const options = [{ id: 'profile', content: translate('Your profile'), onClick: goToProfilePage }];
        const externalLinks = isSiteUser
            ? []
            : [{ id: 'documentation', content: translate('Documentation'), onClick: openDocumentation }];
        if (process.env.ENABLE_DEVELOPER_FLAGS === 'true') {
            options.push({ id: 'developer-flags', content: translate('Developer flags'), onClick: openDeveloperFlags });
        }
        options.push(...externalLinks.map((link) => ({
            id: link.id,
            content: (_jsxs("div", { className: 'flex items-center gap-2', children: [_jsx("span", { children: link.content }), isDocumentationLoading && _jsx(Spinner, { size: 'sm' }), !isDocumentationLoading && _jsx(Icon, { name: 'externalLink', size: 'sm' })] })),
            onClick: link.onClick,
        })));
        return options;
    }, [isDocumentationLoading, isSiteUser, translate]);
    const renderHeaderLink = useCallback(({ name, path, linkStyles }) => {
        return (_jsx(Link, { to: path, className: linkStyles, children: name }, path));
    }, [pathname]);
    const shouldRenderRouteContent = hasEvaluatedTimezoneChange && !timezoneChangeDetected;
    return (_jsxs("section", { className: 'flex h-screen flex-col overflow-hidden', children: [timezoneChangeDetected && (_jsx(TimezoneChangeDetectedModal, { onCancel: onCloseTimezoneChange, onConfirm: onConfirmTimezoneChange, onDontShowAgain: onDontShowAgainTimezoneChange, previousTimezone: (_c = user === null || user === void 0 ? void 0 : user.tz) !== null && _c !== void 0 ? _c : '', currentTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone })), _jsx("div", { "data-testid": 'app-header', children: _jsx(Header, { activeLinkPath: sectionPath, dropdownOptions: dropdownOptions, navLinks: headerNavLinks, onSignOut: useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
                        yield authLogout();
                        if (firebaseAuth === null || firebaseAuth === void 0 ? void 0 : firebaseAuth.logout) {
                            yield firebaseAuth.logout();
                        }
                    }), [authLogout, firebaseAuth]), platformVersion: platformVersion, renderLink: renderHeaderLink, selectedWorkspace: entity === null || entity === void 0 ? void 0 : entity.name, switcherEnabled: workspaceOptions.length > 2 || isSiteUser, user: user, workspaceOptions: workspaceOptions, notificationsConfig: {
                        enabled: isAgentsAppEnabled,
                        hasUnread: false,
                        isActive: isNotificationsPage,
                        onClick: onNotificationsClick,
                    } }) }), _jsxs("div", { className: 'flex flex-col grow overflow-hidden', children: [isConfigurationLoading && _jsx(Spinner, { wrapper: 'full' }), shouldRenderRouteContent ? (_jsxs(_Fragment, { children: [_jsx(AppBanner, {}), navItems && (_jsx("div", { className: 'flex flex-col bg-surface-sunken', "data-testid": 'side-nav-menu', children: _jsx(SideNav, { activeLinkPath: pathname.replace(`${sectionPath}/`, ''), items: navItems, LinkComponent: Link }) })), _jsx("div", { className: 'flex h-full w-full grow flex-col overflow-hidden bg-background', children: _jsx(Suspense, { fallback: _jsx("section", { className: 'flex items-center justify-center h-full', children: _jsx(Spinner, {}) }), children: _jsx(Outlet, {}) }) })] })) : null] })] }));
};
